import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { Phone, Mail, Clock, MapPin, ArrowRight, Instagram, Facebook, Music2, Twitter, Youtube, ShieldCheck, Check } from 'lucide-react';
export function Footer() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].footer;
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 3000);
    }
  };
  return <footer className="bg-mf-black text-white relative" data-id="element-154">
      {/* Top Red Help Bar */}
      <div className="bg-mf-red py-8 relative overflow-hidden" data-id="element-155">
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(#FFFFFF 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }} data-id="element-156"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-157">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-158">
            <div data-id="element-159">
              <h3 className="text-2xl md:text-4xl font-condensed font-black uppercase tracking-tight text-white" data-id="element-160">
                {text.helpBar}
              </h3>
              <p className="text-white/90 mt-1 text-sm md:text-base font-medium flex items-center" data-id="element-161">
                <ShieldCheck size={16} className="mr-2" data-id="element-162" />
                {text.helpSub}
              </p>
            </div>
            <a href="tel:+18134768935" className="inline-flex items-center justify-center bg-white text-mf-red px-8 py-4 rounded-2xl font-condensed font-black text-2xl tracking-wide hover:bg-mf-yellow hover:text-mf-black transition-colors shadow-xl whitespace-nowrap" data-id="element-163">
              <Phone size={24} className="mr-3" data-id="element-164" />
              (813) 476-8935
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-165">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-166">
          {/* Brand Column */}
          <div className="lg:col-span-4" data-id="element-167">
            <div className="flex items-center gap-4 mb-5" data-id="element-168">
              <img src="https://cdn.magicpatterns.com/uploads/87bxoPCzmrJpV9TJfPPsjR/IMG_4659.jpg" alt="Move Fighters" className="w-16 h-16 object-cover rounded-xl shadow-lg" data-id="element-169" />
              <div data-id="element-170">
                <div className="text-2xl font-condensed font-black uppercase tracking-tight" data-id="element-171">
                  MOVE FIGHTERS
                </div>
                <div className="text-sm text-gray-400" data-id="element-172">{text.tagline}</div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 mb-8" data-id="element-173">
              {text.badges.map((badge, i) => <span key={i} className="inline-flex items-center bg-white/5 border border-white/10 px-3 py-1.5 rounded-full text-xs text-gray-300 font-medium" data-id="element-174">
                  <Check size={12} className="mr-1.5 text-green-400" data-id="element-175" />
                  {badge}
                </span>)}
            </div>

            {/* Newsletter */}
            <div data-id="element-176">
              <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-3 text-sm" data-id="element-177">
                {text.newsletterTitle}
              </h4>
              <form onSubmit={handleSubscribe} className="flex gap-2 mb-2" data-id="element-178">
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder={text.emailPlaceholder} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-mf-red transition-colors" data-id="element-179" />
                <button type="submit" className="bg-mf-red hover:bg-red-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors flex items-center" data-id="element-180">
                  {subscribed ? <Check size={16} data-id="element-181" /> : <ArrowRight size={16} data-id="element-182" />}
                </button>
              </form>
              <p className="text-xs text-gray-500" data-id="element-183">{text.noSpam}</p>
            </div>
          </div>

          {/* Services Column */}
          <div className="lg:col-span-2" data-id="element-184">
            <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-6 text-sm border-b border-white/10 pb-3" data-id="element-185">
              {text.servicesTitle}
            </h4>
            <ul className="space-y-3" data-id="element-186">
              {text.servicesLinks.map((link, i) => <li key={i} data-id="element-187">
                  <a href="#services" className="text-sm text-gray-400 hover:text-mf-yellow transition-colors flex items-center group" data-id="element-188">
                    <span className="w-1 h-1 rounded-full bg-mf-red mr-2 group-hover:w-2 transition-all" data-id="element-189"></span>
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Legal & Company Column */}
          <div className="lg:col-span-3" data-id="element-190">
            <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-6 text-sm border-b border-white/10 pb-3" data-id="element-191">
              {text.legalTitle}
            </h4>
            <ul className="space-y-3 mb-8" data-id="element-192">
              {text.legalLinks.map((link, i) => <li key={i} data-id="element-193">
                  <a href="#" className="text-sm text-gray-400 hover:text-mf-yellow transition-colors flex items-center group" data-id="element-194">
                    <span className="w-1 h-1 rounded-full bg-mf-red mr-2 group-hover:w-2 transition-all" data-id="element-195"></span>
                    {link}
                  </a>
                </li>)}
            </ul>

            <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-6 text-sm border-b border-white/10 pb-3" data-id="element-196">
              {text.companyTitle}
            </h4>
            <ul className="space-y-3" data-id="element-197">
              {text.companyLinks.map((link, i) => <li key={i} data-id="element-198">
                  <a href="#" className="text-sm text-gray-400 hover:text-mf-yellow transition-colors flex items-center group" data-id="element-199">
                    <span className="w-1 h-1 rounded-full bg-mf-red mr-2 group-hover:w-2 transition-all" data-id="element-200"></span>
                    {link}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="lg:col-span-3" data-id="element-201">
            <h4 className="font-condensed font-black uppercase tracking-wider text-white mb-6 text-sm border-b border-white/10 pb-3" data-id="element-202">
              {text.contactTitle}
            </h4>

            <ul className="space-y-5 mb-6" data-id="element-203">
              <li data-id="element-204">
                <a href="tel:+18134768935" className="flex items-start group" data-id="element-205">
                  <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mr-3 group-hover:bg-mf-red group-hover:border-mf-red transition-colors flex-shrink-0" data-id="element-206">
                    <Phone size={16} className="text-mf-red group-hover:text-white" data-id="element-207" />
                  </div>
                  <div data-id="element-208">
                    <div className="font-bold text-white text-base" data-id="element-209">
                      (813) 476-8935
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-210">{text.callNow}</div>
                  </div>
                </a>
              </li>

              <li data-id="element-211">
                <a href="mailto:movefighters@gmail.com" className="flex items-start group" data-id="element-212">
                  <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mr-3 group-hover:bg-mf-red group-hover:border-mf-red transition-colors flex-shrink-0" data-id="element-213">
                    <Mail size={16} className="text-mf-red group-hover:text-white" data-id="element-214" />
                  </div>
                  <div data-id="element-215">
                    <div className="font-bold text-white text-sm break-all" data-id="element-216">
                      movefighters@gmail.com
                    </div>
                  </div>
                </a>
              </li>

              <li className="flex items-start" data-id="element-217">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-218">
                  <Clock size={16} className="text-mf-red" data-id="element-219" />
                </div>
                <div data-id="element-220">
                  <div className="font-bold text-white text-sm" data-id="element-221">
                    {text.hours}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-222">
                    {text.hoursSunday}
                  </div>
                </div>
              </li>

              <li className="flex items-start" data-id="element-223">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-224">
                  <MapPin size={16} className="text-mf-red" data-id="element-225" />
                </div>
                <div data-id="element-226">
                  <div className="font-bold text-white text-sm" data-id="element-227">
                    {text.address}
                  </div>
                </div>
              </li>
            </ul>

            {/* Social Media */}
            <div className="flex gap-2 mb-4" data-id="element-228">
              <SocialIcon icon={<Instagram size={16} data-id="element-230" />} href="#" data-id="element-229" />
              <SocialIcon icon={<Facebook size={16} data-id="element-232" />} href="#" data-id="element-231" />
              <SocialIcon icon={<Music2 size={16} data-id="element-234" />} href="#" data-id="element-233" />
              <SocialIcon icon={<Youtube size={16} data-id="element-236" />} href="#" data-id="element-235" />
              <SocialIcon icon={<Twitter size={16} data-id="element-238" />} href="#" data-id="element-237" />
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-239">
          <div className="flex items-center gap-3" data-id="element-240">
            <span className="text-xs font-condensed font-bold uppercase tracking-widest text-gray-500" data-id="element-241">
              {text.paymentTitle}:
            </span>
            <div className="flex items-center gap-2" data-id="element-242">
              <PaymentLogo type="visa" data-id="element-243" />
              <PaymentLogo type="mastercard" data-id="element-244" />
              <PaymentLogo type="amex" data-id="element-245" />
              <PaymentLogo type="discover" data-id="element-246" />
              <PaymentLogo type="apple" data-id="element-247" />
              <PaymentLogo type="cash" data-id="element-248" />
            </div>
          </div>

          <div className="text-xs text-gray-500 flex items-center" data-id="element-249">
            <ShieldCheck size={14} className="mr-2 text-green-400" data-id="element-250" />
            <span data-id="element-251">USDOT • MC • FL Mover Registration</span>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 bg-black/40" data-id="element-252">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-253">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-254">
            <p className="text-sm text-gray-500 text-center md:text-left" data-id="element-255">
              {text.rights}
            </p>
            <div className="flex items-center gap-6 text-xs text-gray-500" data-id="element-256">
              {text.bottomLinks.map((link, i) => <a key={i} href="#" className="hover:text-white transition-colors" data-id="element-257">
                  {link}
                </a>)}
            </div>
          </div>
        </div>
      </div>
    </footer>;
}
// ----- Sub-components -----
function SocialIcon({
  icon,
  href
}: {
  icon: React.ReactNode;
  href: string;
}) {
  return <a href={href} className="w-9 h-9 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:bg-mf-red hover:border-mf-red hover:text-white transition-colors" data-id="element-258">
      {icon}
    </a>;
}
function PaymentLogo({
  type
}: {
  type: 'visa' | 'mastercard' | 'amex' | 'discover' | 'apple' | 'cash';
}) {
  const styles = 'h-7 px-2 bg-white rounded flex items-center justify-center text-[10px] font-black tracking-tight shadow-sm';
  switch (type) {
    case 'visa':
      return <div className={`${styles} text-blue-700 italic font-serif`} data-id="element-259">VISA</div>;
    case 'mastercard':
      return <div className="h-7 w-12 bg-white rounded flex items-center justify-center shadow-sm relative overflow-hidden" data-id="element-260">
          <div className="w-4 h-4 bg-red-500 rounded-full absolute left-1.5" data-id="element-261"></div>
          <div className="w-4 h-4 bg-yellow-400 rounded-full absolute right-1.5 mix-blend-multiply" data-id="element-262"></div>
        </div>;
    case 'amex':
      return <div className={`${styles} text-blue-600`} data-id="element-263">AMEX</div>;
    case 'discover':
      return <div className={`${styles} text-orange-500`} data-id="element-264">DISC</div>;
    case 'apple':
      return <div className={`${styles} text-black`} data-id="element-265">🍎 Pay</div>;
    case 'cash':
      return <div className={`${styles} text-green-700`} data-id="element-266">CASH</div>;
  }
}